import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import type { HttpEvent } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthenticationService } from './services/authentication.service';
import { DebugService } from './services/debug.service';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private authService = inject(AuthenticationService);
  private debug = inject(DebugService);

  private isRefreshing = false;
  /** null = idle; true = refresh OK; false = refresh falló */
  private refreshDone$ = new BehaviorSubject<boolean | null>(null);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    this.debug.log('🛡️ Interceptando:', request.url);

    if (
      request.url.includes('/auth/refresh') ||
      request.url.includes('/auth/logout') ||
      request.url.includes('/auth/login')
    ) {
      return next.handle(request.clone({ withCredentials: true }));
    }

    const reqWithCredentials = request.clone({ withCredentials: true });

    return next.handle(reqWithCredentials).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }

        // Sin hint local: no hay sesión que renovar
        if (!this.authService.hasLocalSessionHint()) {
          return throwError(() => error);
        }

        this.debug.log('🔄 401 detectado, intentando refresh...');

        if (this.isRefreshing) {
          return this.refreshDone$.pipe(
            filter((done): done is boolean => done !== null),
            take(1),
            switchMap((ok) => {
              if (!ok) {
                return throwError(() => error);
              }
              return next.handle(reqWithCredentials);
            }),
          );
        }

        this.isRefreshing = true;
        this.refreshDone$.next(null);

        return this.authService.refresh().pipe(
          switchMap(() => {
            this.debug.log('✅ Refresh exitoso, reintentando petición');
            this.authService.markSessionValid();
            this.isRefreshing = false;
            this.refreshDone$.next(true);
            return next.handle(reqWithCredentials);
          }),
          catchError((refreshError) => {
            this.debug.log('❌ Refresh fallido, cerrando sesión');
            this.isRefreshing = false;
            this.refreshDone$.next(false);
            this.authService.logout(true);
            return throwError(() => refreshError);
          }),
        );
      }),
    );
  }
}
